import type { FC, ReactNode } from 'react'
import type { HeroStats } from '@/components/landing/hero-scene'

import dynamic from 'next/dynamic'
import { useCallback, useRef } from 'react'
import { ArrowRight, Check, MousePointerClick } from 'lucide-react'

import { heroHighlights } from '@/constants/landing'

const HeroScene = dynamic(() => import('@/components/landing/hero-scene'), { ssr: false })

export const HeroSection: FC = (): ReactNode => {
    const energyRef = useRef<HTMLSpanElement>(null)
    const impactsRef = useRef<HTMLSpanElement>(null)
    const bodiesRef = useRef<HTMLSpanElement>(null)

    const handleStats = useCallback((stats: HeroStats) => {
        if (energyRef.current) energyRef.current.textContent = stats.energy.toFixed(2)
        if (impactsRef.current) impactsRef.current.textContent = String(Math.round(stats.impacts))
        if (bodiesRef.current) bodiesRef.current.textContent = String(stats.bodies)
    }, [])

    return (
        <section id='hero' className='relative isolate overflow-hidden'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 -z-10 bg-dots opacity-80 [mask-image:radial-gradient(ellipse_75%_65%_at_62%_45%,black_20%,transparent_75%)]'
            />

            <div className='relative z-10 mx-auto max-w-7xl px-5 pt-32 sm:px-8 sm:pt-36 lg:pointer-events-none lg:flex lg:min-h-[max(100svh,760px)] lg:items-center lg:pb-28 lg:pt-28'>
                <div className='max-w-xl lg:max-w-[580px]'>
                    <a
                        href='#simulations'
                        className='rise-in group inline-flex items-center gap-2.5 rounded-full border border-border/80 bg-white/80 py-1.5 pl-2 pr-3.5 text-[13px] text-muted-foreground elevate backdrop-blur transition-colors hover:text-ink lg:pointer-events-auto'
                    >
                        <span className='inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-2 py-0.5 font-medium text-brand'>
                            <span className='relative flex size-1.5'>
                                <span className='absolute inline-flex size-full animate-ping rounded-full bg-brand opacity-60' />
                                <span className='relative inline-flex size-1.5 rounded-full bg-brand' />
                            </span>
                            Early access
                        </span>
                        140+ simulations, all running live
                        <ArrowRight className='size-3.5 transition-transform group-hover:translate-x-0.5' />
                    </a>

                    <h1
                        className='rise-in mt-7 font-heading text-[clamp(3.1rem,7.6vw,6.6rem)] font-semibold leading-[0.92] tracking-[-0.045em] text-ink lg:glow-white'
                        style={{ animationDelay: '80ms' }}
                    >
                        Physics you
                        <br />
                        can <span className='font-serif font-normal italic tracking-[-0.015em] text-brand'>touch.</span>
                    </h1>

                    <p
                        className='rise-in mt-7 max-w-[31rem] text-lg leading-relaxed text-muted-foreground sm:text-xl lg:glow-white'
                        style={{ animationDelay: '180ms' }}
                    >
                        Inertia turns the laws of nature into real-time simulations you can push, pull and pick apart. Change a variable and watch the equations change with it.
                    </p>

                    <div className='rise-in mt-9 flex flex-wrap items-center gap-3' style={{ animationDelay: '280ms' }}>
                        <a
                            href='#access'
                            className='group inline-flex h-12 items-center gap-2 rounded-full bg-ink pl-6 pr-5 text-[15px] font-medium text-white shadow-[0_14px_30px_-12px_rgba(13,14,20,0.55)] transition-colors hover:bg-ink/85 lg:pointer-events-auto'
                        >
                            Get early access
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </a>

                        <a
                            href='#lab'
                            className='inline-flex h-12 items-center gap-2 rounded-full border border-border bg-white/80 px-6 text-[15px] font-medium text-ink backdrop-blur transition-colors hover:border-ink/25 hover:bg-white lg:pointer-events-auto'
                        >
                            Try the live lab
                        </a>
                    </div>

                    <ul className='rise-in mt-10 flex flex-wrap gap-x-6 gap-y-2.5 text-sm text-muted-foreground' style={{ animationDelay: '380ms' }}>
                        {heroHighlights.map(item => (
                            <li key={item} className='flex items-center gap-2'>
                                <span className='flex size-5 items-center justify-center rounded-full bg-brand-soft text-brand'>
                                    <Check className='size-3' strokeWidth={3} />
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                <a
                    href='#simulations'
                    className='absolute bottom-10 left-8 hidden items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-ink lg:pointer-events-auto lg:flex'
                >
                    <span className='h-px w-10 bg-border' />
                    Scroll to explore
                </a>
            </div>

            <div className='relative h-[400px] sm:h-[480px] lg:absolute lg:inset-0 lg:z-0 lg:h-auto'>
                <HeroScene onStats={handleStats} />
            </div>

            <div className='pointer-events-none relative z-10 mx-auto -mt-10 w-full max-w-sm px-4 pb-12 sm:max-w-md lg:absolute lg:bottom-10 lg:right-10 lg:mt-0 lg:w-auto lg:max-w-none lg:px-0 lg:pb-0'>
                <div className='rise-in rounded-2xl border border-white/70 bg-white/70 p-4 elevate backdrop-blur-xl lg:w-[320px]' style={{ animationDelay: '600ms' }}>
                    <div className='flex items-center justify-between'>
                        <span className='flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink'>
                            <span className='relative flex size-2'>
                                <span className='absolute inline-flex size-full animate-ping rounded-full bg-brand opacity-60' />
                                <span className='relative inline-flex size-2 rounded-full bg-brand' />
                            </span>
                            Live simulation
                        </span>

                        <span className='font-mono text-[11px] text-muted-foreground'>
                            Fig. 0
                        </span>
                    </div>

                    <dl className='mt-3.5 grid grid-cols-3 gap-3'>
                        <div>
                            <dt className='text-[11px] text-muted-foreground'>Bodies</dt>
                            <dd className='mt-0.5 font-mono text-sm tabular-nums text-ink'>
                                <span ref={bodiesRef}>38</span>
                            </dd>
                        </div>

                        <div>
                            <dt className='text-[11px] text-muted-foreground'>Kinetic energy</dt>
                            <dd className='mt-0.5 font-mono text-sm tabular-nums text-ink'>
                                <span ref={energyRef}>0.00</span> J
                            </dd>
                        </div>

                        <div>
                            <dt className='text-[11px] text-muted-foreground'>Impacts / s</dt>
                            <dd className='mt-0.5 font-mono text-sm tabular-nums text-ink'>
                                <span ref={impactsRef}>0</span>
                            </dd>
                        </div>
                    </dl>

                    <p className='mt-3.5 flex items-center gap-2 border-t border-border/70 pt-3 text-xs text-muted-foreground'>
                        <MousePointerClick className='size-4 shrink-0 text-ink' />
                        <span className='lg:hidden'>Drag across the spheres or tap to add energy.</span>
                        <span className='hidden lg:inline'>Move to disturb the cluster, click to add energy.</span>
                    </p>
                </div>
            </div>
        </section>
    )
}
